import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "./Icon";
import { useLocalePath } from "../app/localization";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import i18n from "../i18n";
import { trackAnalyticsEvent } from "../analytics/analytics";

export default function Header() {
  const path = useLocalePath();
  const currentURL = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("header");
  const [isOpen, setIsOpen] = useState(false); // mobile menu
  const [servicesOpen, setServicesOpen] = useState(false); // desktop submenu
  const servicesRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileDialogRef = useRef<HTMLDivElement>(null);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const servicesMenuId = "desktop-services-menu";
  const mobileMenuTitleId = "mobile-menu-title";

  // nav items (use translations instead of literals)
  const navItems = [
    { to: path("/"), label: t("nav.home") },
    { to: path("about"), label: t("nav.about") },
    // Services is handled separately
    { to: path("cases"), label: t("nav.cases") },
    { to: path("contact"), label: t("nav.contact") },
  ];

  const services = [
    { to: path("services/websites"), label: t("services.nalosoft_websites") },
    {
      to: path("services/booking"),
      label: t("services.nalosoft_booking"),
    },
    { to: path("services/webshop"), label: t("services.nalosoft_webshop") },
    {
      to: path("services/mobile-apps"),
      label: t("services.nalosoft_mobile_app"),
    },
    {
      to: path("services/custom-solutions"),
      label: t("services.nalosoft_custom"),
    },
  ];

  // Close desktop dropdown on outside click / ESC
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!servicesRef.current?.contains(e.target as Node))
        setServicesOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setServicesOpen(false);
    }
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    previouslyFocusedElement.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    window.setTimeout(() => mobileCloseButtonRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
      previouslyFocusedElement.current?.focus();
    };
  }, [isOpen]);

  function closeMobileMenu() {
    setIsOpen(false);
  }

  function onMobileDialogKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      closeMobileMenu();
      return;
    }

    if (e.key !== "Tab" || !mobileDialogRef.current) return;

    const focusable = Array.from(
      mobileDialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (element) =>
        !element.hasAttribute("disabled") &&
        element.getAttribute("aria-hidden") !== "true"
    );

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Announcement text with a variable
  // Localized month for EPP ("August", "august", etc.), capitalized first letter
  const monthFmt = new Intl.DateTimeFormat(
    i18n.language?.startsWith("da") ? "da-DK" : "en",
    { month: "long" }
  ).format(new Date());
  const month = monthFmt.replace(/^./, (c) => c.toUpperCase());
  const announcementText = t("announcement", { month });

  // ANNOUNCEMENT BAR (toggle with enabled)
  const announcement = {
    enabled: false,
    href: path("/early-partner-program"), // where to go when clicked
    text: announcementText,
  };

  const onboardHref = path("/onboard");

  function onOnboardClick(e: ReactMouseEvent<HTMLAnchorElement>) {
    trackAnalyticsEvent("book_meeting_click", {
      location: "header",
      destination: "onboard",
    });

    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.altKey ||
      e.ctrlKey ||
      e.shiftKey
    ) {
      return;
    }

    const transitionDocument = document as Document & {
      startViewTransition?: (callback: () => void) => void;
    };

    if (!transitionDocument.startViewTransition) return;

    e.preventDefault();
    transitionDocument.startViewTransition(() => {
      navigate(onboardHref);
    });
  }

  function onMobileOnboardClick(e: ReactMouseEvent<HTMLAnchorElement>) {
    onOnboardClick(e);
    closeMobileMenu();
  }

  return (
    <>
      {announcement.enabled && (
        <Link
          to={announcement.href}
          className="block w-full bg-slate-950 text-white transition-colors duration-200 hover:bg-slate-800"
        >
          <div className="mx-auto max-w-[1180px] px-4 py-2 text-center text-sm font-medium sm:px-6 lg:px-8">
            {announcement.text}
          </div>
        </Link>
      )}

      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950 text-white backdrop-blur">
        <div className="mx-auto max-w-[1180px] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-[1fr,auto,1fr] items-center h-16">
            {/* Logo */}
            <div className="justify-self-start">
              <Link to={path("/")} className="flex items-center space-x-2">
                <picture>
                  <source srcSet="/optimized/logo.avif" type="image/avif" />
                  <source srcSet="/optimized/logo.webp" type="image/webp" />
                  <img
                    src="/logo.png"
                    alt="Nalosoft logo"
                    width={380}
                    height={75}
                    decoding="async"
                    loading="eager"
                    className="h-7 w-auto"
                  />
                </picture>
              </Link>
            </div>
            {/* Desktop Nav */}
            <nav
              aria-label={t("aria.primaryNavigation")}
              className="hidden md:flex items-center space-x-6 justify-self-center text-sm font-medium"
            >
              {navItems.slice(0, 2).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.label === t("nav.home")}
                  className={({ isActive }) =>
                    `transition hover:text-white ${
                      isActive ? "font-semibold text-white" : "text-white/80"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              {/* Services dropdown (desktop) */}
              <div
                className="relative"
                ref={servicesRef}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setServicesOpen((v) => !v)}
                  className={`inline-flex items-center gap-1 text-sm font-medium transition hover:text-white ${
                    servicesOpen
                      ? "text-white"
                      : currentURL.pathname.includes("/services")
                      ? "font-semibold text-white"
                      : "text-white/80"
                  }`}
                  aria-expanded={servicesOpen}
                  aria-controls={servicesMenuId}
                >
                  {t("nav.services")}
                  <svg
                    aria-hidden="true"
                    className={`h-4 w-4 transition-transform mt-[2px] md:mr-[-5px] ${
                      servicesOpen ? "rotate-180" : ""
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
                  </svg>
                </button>

                {servicesOpen && (
                  <div
                    id={servicesMenuId}
                    className="absolute left-0 mt-3 w-64 rounded-lg border border-slate-200 bg-white p-2 text-slate-900 shadow-lg z-50 before:absolute before:-top-3 before:left-0 before:h-3 before:w-full before:bg-transparent"
                  >
                    <div className="grid grid-cols-1">
                      {services.map((s) => (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-950"
                          onClick={() => setServicesOpen(false)}
                        >
                          {s.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {navItems.slice(2).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `transition hover:text-white ${
                      isActive ? "font-semibold text-white" : "text-white/80"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="justify-self-end hidden md:flex items-center gap-3">
              {/* Language switcher */}
              <LanguageSwitcher />

              {/* Get started button */}
              <Link
                to={onboardHref}
                onClick={onOnboardClick}
                className="group inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-slate-200"
              >
                {t("nav.onboard")}
                <Icon
                  icon="mdi:arrow-right"
                  width={16}
                  height={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
            </div>
            {/* This is to have the mobile button at last column */}
            <div className="md:hidden"></div>
            {/* Mobile menu button */}
            <div className="md:hidden justify-self-end items-center">
              <button
                type="button"
                ref={mobileMenuButtonRef}
                className="rounded-md p-2 text-white hover:bg-white/10"
                onClick={() => setIsOpen((v) => !v)}
                aria-label={isOpen ? t("aria.closeMenu") : t("aria.openMenu")}
                aria-expanded={isOpen}
                aria-controls="mobile-menu-dialog"
              >
                <svg
                  aria-hidden="true"
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  {isOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer (right-side) */}
      {isOpen && (
        <div
          id="mobile-menu-dialog"
          ref={mobileDialogRef}
          className="fixed inset-0 z-[80] flex md:hidden"
          onKeyDown={onMobileDialogKeyDown}
        >
          {/* 15% dim backdrop on the left – click to close */}
          <button
            type="button"
            aria-label={t("aria.closeMenuBackdrop")}
            onClick={closeMobileMenu}
            className="min-w-[15vw] flex-1 bg-slate-950/35"
          />

          {/* 85% sliding panel from the right */}
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={mobileMenuTitleId}
            className="flex h-full min-h-0 w-[85vw] max-w-sm shrink-0 flex-col overflow-hidden bg-white text-slate-950 shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-[18px] py-3">
              <span id={mobileMenuTitleId} className="text-xl font-semibold">
                {t("nav.menu")}
              </span>
              <button
                type="button"
                ref={mobileCloseButtonRef}
                className="md:hidden rounded-md p-2 hover:bg-slate-100"
                onClick={closeMobileMenu}
                aria-label={t("aria.closeMenu")}
              >
                <svg
                  aria-hidden="true"
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <nav
              aria-label={t("aria.mobileNavigation")}
              className="min-h-0 flex-1 space-y-5 overflow-y-auto px-[18px] py-6 text-base font-medium"
            >
              {navItems.slice(0, 2).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.label === t("nav.home")}
                  onClick={closeMobileMenu}
                  className="block text-slate-700 hover:text-slate-950"
                >
                  {item.label}
                </NavLink>
              ))}

              {/* Services accordion (mobile) */}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center justify-between text-slate-700 hover:text-slate-950">
                  <span>{t("nav.services")}</span>
                  <svg
                    aria-hidden="true"
                    className="h-4 w-4 transition-transform group-open:rotate-180"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
                  </svg>
                </summary>
                <div className="pl-3 pt-3 space-y-3">
                  {services.map((s) => (
                    <NavLink
                      key={s.to}
                      to={s.to}
                      onClick={closeMobileMenu}
                      className="block text-sm text-slate-600 hover:text-slate-950"
                    >
                      <div className="flex items-center">
                        <Icon
                          aria-hidden="true"
                          icon="mdi:circle"
                          className="text-slate-400"
                          height={8}
                          width={8}
                        />
                        <span className="pl-2 text-base">{s.label}</span>
                      </div>
                    </NavLink>
                  ))}
                </div>
              </details>

              {navItems.slice(2).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeMobileMenu}
                  className="block text-slate-700 hover:text-slate-950"
                >
                  {item.label}
                </NavLink>
              ))}

              <div className="space-y-4 border-t border-slate-200 pt-5">
                <Link
                  to={onboardHref}
                  onClick={onMobileOnboardClick}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  {t("nav.onboard")}
                  <Icon
                    icon="mdi:arrow-right"
                    width={16}
                    height={16}
                    className="transition-transform group-hover:translate-x-0.5"
                  />
                </Link>
                <div className="flex justify-start">
                  <LanguageSwitcher />
                </div>
              </div>
            </nav>

            <nav
              aria-label={t("aria.socialLinks")}
              className="w-full shrink-0 border-t border-slate-200"
            >
              <div className="flex items-center space-x-14 py-6 mx-auto w-max">
                <a
                  href="https://www.facebook.com/nalosoft"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-500 hover:text-slate-950"
                  aria-label="Facebook"
                >
                  <Icon icon="basil:facebook-solid" width={20} height={20} />
                </a>
                <a
                  href="https://www.instagram.com/nalosoftcom"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-500 hover:text-slate-950"
                  aria-label="Instagram"
                >
                  <Icon icon="basil:instagram-solid" width={20} height={20} />
                </a>
                <a
                  href="https://x.com/nalosoft"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-500 hover:text-slate-950"
                  aria-label="X"
                >
                  <Icon icon="fa7-brands:x-twitter" width={18} height={17} />
                </a>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
